import { useAddSignal } from '../hooks/useAddSignal';
import dayjs from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { PiCaretDownLight } from 'react-icons/pi';
import CalendarView from '../components/Tasks/CalendarView';
import { useFeatureTour } from '../hooks/useFeatureTour';
import { FeatureTour, calendarTourSteps } from '../components/shared/FeatureTour';
import {
  fetchCalendarEvents,
  createCalendarEvent,
  updateCalendarEvent,
  exportCalendarICS,
  importCalendarICS,
  syncCalendarEvents,
  createFeedToken,
  setCalendarFilters,
} from '../store/actions/CalendarActions';
import { showModal, hideModal } from '../store/actions/modalActions';
import { useHistory, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/index';
import {
  CalendarEvent,
  CalendarEventType,
  CalendarSourceType,
  EVENT_TYPE_COLORS,
} from '../components/Calendar/types';
import { Action } from '../components/shared/TailwindComponents/Header';
import {
  PiDownloadSimpleLight,
  PiUploadSimpleLight,
  PiArrowsClockwiseLight,
  PiLinkLight,
  PiCalendarBlankLight,
  PiMapPinLight,
  PiClockLight,
  PiTagLight,
  PiArrowSquareOutLight,
  PiXLight,
  PiUsersLight,
  PiInfoLight,
  PiNotePencilLight,
  PiCopyLight,
  PiBellLight,
} from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import Badge from '../components/shared/TailwindComponents/Badge';
import Button from '../components/shared/TailwindComponents/Button';
import Dropdown from '../components/shared/TailwindComponents/Dropdown';
import Input from '../components/shared/TailwindComponents/Input';
import Textarea from '../components/shared/TailwindComponents/Textarea';
import Toggle from '../components/shared/TailwindComponents/Toggle';
import DateTimePicker from '../components/shared/TailwindComponents/DatePicker';
import GlassCard from '../components/shared/brand/GlassCard';
import { FactTile, FieldGrid } from '../components/shared/brand/Field';
import ProfileImage from '../components/Header/ProfileImage';
import { formatDateTime } from '../utils/functions';
import { normalise } from '../components/shared/access/accessModel';

dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);

/* ─── Displayable event type labels ─── */
const EVENT_TYPE_LABELS: Partial<Record<CalendarEventType, string>> = {
  task_deadline: 'Task',
  matter_deadline: 'Matter',
  project_deadline: 'Project',
  hearing: 'Hearing',
  meeting: 'Meeting',
  consultation: 'Consultation',
  filing_deadline: 'Filing',
  statute_of_limitations: 'Limitation',
  client_meeting: 'Client',
  court_date: 'Court',
  reminder: 'Reminder',
  custom: 'Custom',
  task: 'Task',
  matter: 'Matter',
  project: 'Project',
};

const LEGEND_TYPES: CalendarEventType[] = [
  'task_deadline', 'matter_deadline', 'project_deadline', 'reminder', 'custom',
];

const FILTER_EVENT_TYPES: CalendarEventType[] = [
  'task_deadline', 'matter_deadline', 'project_deadline', 'hearing', 'meeting', 'consultation',
  'filing_deadline', 'statute_of_limitations', 'client_meeting', 'court_date', 'reminder', 'custom',
];

const SOURCE_TYPES: CalendarSourceType[] = ['task', 'matter', 'project', 'client', 'manual'];

/* ═══════════════════════════════════════════════════════════════════════
   Event Detail Modal Content
   ═══════════════════════════════════════════════════════════════════════ */

interface EventDetailProps {
  event: CalendarEvent;
  onNavigate: (event: CalendarEvent) => void;
  onClose: () => void;
}

const EventDetailContent: React.FC<EventDetailProps> = ({ event, onNavigate, onClose }) => {
  const { t } = useTranslation();
  const colors = EVENT_TYPE_COLORS[event.event_type] ?? EVENT_TYPE_COLORS.custom;
  const typeLabel = EVENT_TYPE_LABELS[event.event_type] ?? event.event_type;
  const [peopleOpen, setPeopleOpen] = useState(true);

  const hasSource = event.source_type && event.source_uuid;
  const sourceLabel = event.source_type === 'task'
    ? t('View task')
    : event.source_type === 'matter'
      ? t('View matter')
      : event.source_type === 'project'
        ? t('View project')
        : t('View source');

  const startLabel = event.start_time ? formatDateTime(event.start_time) : '';
  const endLabel = event.end_time ? formatDateTime(event.end_time) : '';

  // Merge shared_with + collaborators, dedup by user_id/email
  const peopleRaw = [
    ...((event as any).collaborators || []),
    ...(event.shared_with || []),
  ];
  const seen = new Set<string>();
  const people = peopleRaw.filter((p: any) => {
    const key = String(p.user_id ?? p.uuid ?? p.email ?? p.Email ?? p.Name ?? Math.random());
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return (
    <div className="w-full max-w-2xl mx-auto flex flex-col max-h-[75vh] min-h-[75vh]">
      {/* Header (sticky / non-scrolling) */}
      <div className={`flex items-start gap-3 p-4 rounded-lg mx-6 flex-shrink-0 ring-1 ring-inset ${colors.chip} ${colors.ring}`}>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-medium text-fg-strong leading-tight break-words">
            {event.title}
          </h2>
          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            {/* The type reads in the calendar's own palette, not a second one. */}
            <span className={`inline-flex items-center gap-1.5 rounded-full bg-surface/70 px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset ${colors.ring} ${colors.text}`}>
              <span className={`h-1.5 w-1.5 rounded-full ${colors.dot}`} />
              {typeLabel}
            </span>
            {event.status && event.status !== 'active' && (
              <Badge color={event.status === 'completed' ? 'green' : event.status === 'cancelled' ? 'red' : 'yellow'}>
                {event.status}
              </Badge>
            )}
            {event.visibility && event.visibility !== 'private' && (
              <Badge color="gray">{event.visibility}</Badge>
            )}
          </div>
        </div>
      </div>

      {/* Scrollable middle */}
      <div className="flex-1 overflow-y-auto custom-scrollbar-main px-6 pt-4 pb-2 min-h-0">

      {/* Detail grid */}
      <FieldGrid columns={2}>
        {startLabel && (
          <FactTile icon={<PiClockLight />} label={t('When')}>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {startLabel}
              {endLabel && !event.all_day && (
                <span className="text-fg-muted"> - {endLabel}</span>
              )}
            </p>
            {!event.all_day && (
              <p className="text-[11px] text-fg-muted mt-1">
                {dayjs.tz?.guess?.() || ''}
              </p>
            )}
          </FactTile>
        )}

        {event.location && (
          <FactTile
            icon={<PiMapPinLight />}
            label={t('Location')}
            value={<p className="text-sm font-medium text-gray-700 dark:text-gray-200 truncate" title={event.location}>{event.location}</p>}
          />
        )}

        {hasSource && (
          <FactTile
            icon={<PiCalendarBlankLight />}
            label={t('Source')}
            value={<p className="text-sm font-medium capitalize text-gray-700 dark:text-gray-200">{event.source_type}</p>}
          />
        )}

        {/* REMINDERS - FLAGGED (cal-3). `reminder_minutes` is stored on the
            event and shown here, and that is the whole of it: nothing in the
            front end schedules a notification from it, no PubSub notification
            type carries it, and the Calendar section of notification
            preferences (`calendar_event_reminder`) is still marked
            `isComingSoon`. So the tile says what it is worth rather than
            promising a delivery no channel makes. Remove the caption the day
            the backend dispatches it, and name the channel instead. */}
        {event.reminder_minutes && event.reminder_minutes > 0 ? (
          <FactTile icon={<PiBellLight />} label={t('Reminder')}>
            <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
              {`${event.reminder_minutes} ${t('minutes before')}`}
            </p>
            <p className="mt-1 text-[11px] text-warning-600 dark:text-warning-400">
              {t('Reminders are not delivered yet')}
            </p>
          </FactTile>
        ) : null}
      </FieldGrid>

      {/* Description */}
      {event.description && (
        <GlassCard className="mt-4" icon={<PiNotePencilLight />} title={t('Description')}>
          <p className="text-sm text-gray-700 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
            {event.description}
          </p>
        </GlassCard>
      )}

      {/* People with access */}
      {people.length > 0 && (
        <GlassCard className="mt-4" padding="none">
          <button
            type="button"
            onClick={() => setPeopleOpen((v) => !v)}
            className="w-full flex items-center gap-2 px-4 py-2.5 border-b border-hairline-soft hover:bg-surface-sunken/60 transition-colors text-start"
          >
            <PiUsersLight className="h-4 w-4 text-fg-muted" />
            <span className="text-xs font-medium text-fg-muted flex-1">
              {t('People with access')} ({people.length})
            </span>
            <PiCaretDownLight
              className={`h-4 w-4 text-fg-muted transition-transform ${peopleOpen ? '' : '-rotate-90'}`}
            />
          </button>
          {peopleOpen && (
          <div className="p-2 space-y-1">
            {[...people]
              .sort((a: any, b: any) => {
                const rank = (x: any) => {
                  const r = (x.role || '').toLowerCase();
                  const a2 = (x.access || '').toLowerCase();
                  if (r === 'creator' || r === 'owner' || a2 === 'admin') return 0;
                  if (a2 === 'edit') return 1;
                  return 2;
                };
                return rank(a) - rank(b);
              })
              .map((p: any, i: number) => {
                const name = p.name || p.Name || p.email || p.Email || '';
                const email = p.email || p.Email || '';
                const initials = p.initials
                  || (name ? name.split(' ').map((s: string) => s.charAt(0)).join('').slice(0, 2).toUpperCase() : '??');
                const accessRaw = (p.access || '').toLowerCase();
                const role = (p.role || '').toLowerCase();
                const isOwner = role === 'creator' || role === 'owner' || accessRaw === 'admin';
                const accessLabel = isOwner
                  ? t('Owner')
                  : accessRaw === 'edit'
                    ? t('Edit')
                    : accessRaw === 'view'
                      ? t('View')
                      : accessRaw
                        ? accessRaw.charAt(0).toUpperCase() + accessRaw.slice(1)
                        : '';
                const accessColor: any = isOwner ? 'amber' : accessRaw === 'edit' ? 'blue' : 'gray';
                return (
                  <div
                    key={i}
                    className={`flex items-center gap-2.5 rounded-md px-2 py-1.5 ${
                      isOwner ? 'bg-champagne/10 ring-1 ring-inset ring-champagne/25' : ''
                    }`}
                  >
                    <ProfileImage
                      profilePicture={normalise(p).avatar}
                      email={email}
                      initials={initials}
                      name={name}
                      size="w-8 h-8"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-fg-strong truncate">{name}</p>
                      {email && <p className="text-[11px] text-fg-muted truncate">{email}</p>}
                    </div>
                    {accessLabel && (
                      <Badge color={accessColor}>{accessLabel}</Badge>
                    )}
                  </div>
                );
              })}
          </div>
          )}
        </GlassCard>
      )}

      {/* Tags */}
      {(event.tags?.length || 0) > 0 && (
        <GlassCard className="mt-4" padding="sm" icon={<PiTagLight />} title={t('Tags')} density="compact">
          <div className="flex flex-wrap gap-1.5">
            {event.tags!.map((tag: string) => (
              <Badge key={tag} color="gray">{tag}</Badge>
            ))}
          </div>
        </GlassCard>
      )}

      </div>{/* /Scrollable middle */}

      {/* Actions (sticky at bottom) */}
      <div className="px-6 py-4 border-t border-hairline flex justify-end gap-2 flex-shrink-0">
        <Button variant="neutral" size="small" onClick={onClose}>
          {t('Close')}
        </Button>
        {hasSource && (
          <Button
            variant="primary"
            size="small"
            onClick={() => onNavigate(event)}
            icon={<PiArrowSquareOutLight className="h-4 w-4" />}
          >
            {sourceLabel}
          </Button>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════
   Create Event Modal Content

   The calendar could read events (from tasks, matters, projects and ICS) but
   never create one of its own. The sidebar quick-"+" (UI feedback SM-1) opens
   this: a manual event the user owns, POSTed through `createCalendarEvent`.
   ═══════════════════════════════════════════════════════════════════════ */

/** Event types a person actually creates by hand - the deadline types are
 *  derived from their source record, not entered here. */
const MANUAL_EVENT_TYPES: CalendarEventType[] = [
  'meeting', 'client_meeting', 'consultation', 'hearing', 'court_date',
  'filing_deadline', 'reminder', 'custom',
];

interface CreateEventProps {
  initialDate: dayjs.Dayjs;
  onClose: () => void;
  onCreate: (payload: Partial<CalendarEvent>) => Promise<void>;
}

const CreateEventContent: React.FC<CreateEventProps> = ({ initialDate, onClose, onCreate }) => {
  const { t } = useTranslation();
  // Default to the next full hour on the day the user is looking at.
  const defaultStart = initialDate.minute(0).second(0).add(1, 'hour');
  const [title, setTitle] = useState('');
  const [eventType, setEventType] = useState<CalendarEventType>('meeting');
  const [allDay, setAllDay] = useState(false);
  const [start, setStart] = useState<Date>(defaultStart.toDate());
  const [end, setEnd] = useState<Date | null>(defaultStart.add(1, 'hour').toDate());
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const typeOptions = MANUAL_EVENT_TYPES.map((type) => ({
    value: type,
    label: t(EVENT_TYPE_LABELS[type] ?? type),
  }));

  const canSave = title.trim().length > 0 && !saving;

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const startIso = dayjs(start).toISOString();
      await onCreate({
        title: title.trim(),
        event_type: eventType,
        all_day: allDay,
        start_time: startIso,
        end_time: !allDay && end ? dayjs(end).toISOString() : undefined,
        location: location.trim() || undefined,
        description: description.trim() || undefined,
        status: 'active',
        visibility: 'private',
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-[34rem] max-w-[90vw] space-y-5 px-6 pb-2">
      <Input
        id="create-event-title"
        type="text"
        label={t('Title')}
        placeholder={t('e.g., Client call, filing deadline')}
        value={title}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
        required
      />

      <Dropdown
        id="create-event-type"
        label={t('Type')}
        options={typeOptions}
        value={eventType}
        onChange={(v) => setEventType((Array.isArray(v) ? v[0] : v) as CalendarEventType)}
        fullWidth
        className="w-full"
      />

      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-fg-strong">{t('All day')}</p>
          <p className="text-[11px] text-fg-muted">{t('No specific start and end time.')}</p>
        </div>
        <Toggle enabled={allDay} onToggle={setAllDay} />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <DateTimePicker
          id="create-event-start"
          label={t('Start')}
          selected={start}
          placeholder={t('Start')}
          showTimeSelect={!allDay}
          dateFormat={allDay ? 'yyyy-MM-dd' : 'yyyy-MM-dd HH:mm'}
          fullWidth
          required
          onChange={(d) => { if (d) setStart(d as Date); }}
        />
        {!allDay && (
          <DateTimePicker
            id="create-event-end"
            label={t('End')}
            selected={end}
            placeholder={t('End')}
            showTimeSelect
            dateFormat="yyyy-MM-dd HH:mm"
            fullWidth
            onChange={(d) => setEnd((d as Date) || null)}
          />
        )}
      </div>

      <Input
        id="create-event-location"
        type="text"
        label={t('Location')}
        placeholder={t('Optional')}
        value={location}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLocation(e.target.value)}
      />

      <Textarea
        id="create-event-description"
        label={t('Description')}
        value={description}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDescription(e.target.value)}
        placeholder={t('Optional')}
        rows={3}
      />

      <div className="flex justify-end gap-2 border-t border-hairline pt-4">
        <Button variant="neutral" size="small" onClick={onClose} disabled={saving}>
          {t('Cancel')}
        </Button>
        <Button variant="primary" size="small" onClick={handleSave} disabled={!canSave} isLoading={saving}>
          {t('Create event')}
        </Button>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════
   Calendar View Page
   ═══════════════════════════════════════════════════════════════════════ */

const VIEW_MODES = ['day', 'week', 'month', 'year'] as const;
type CalendarViewMode = (typeof VIEW_MODES)[number];

function CalendarViewPage() {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();

  const { events, loading, filters, syncStatus, feedToken } = useAppSelector(
    (state: any) => state.calendar
  );

  /* The view and the date live in the URL (cal-4). They used to be component
     state only, so anything that left the calendar and came back - a browser
     Back, the header's back caret, a link - landed on today in Month view,
     losing the month you were actually reading. */
  // Read once, on arrival: after that the state below owns the URL, not the
  // other way round, or every replace would re-seed the state it just wrote.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialParams = useMemo(() => new URLSearchParams(location.search), []);
  const [calendarViewMode, setCalendarViewMode] = useState<CalendarViewMode>(() => {
    const fromUrl = initialParams.get('view') as CalendarViewMode | null;
    return fromUrl && VIEW_MODES.includes(fromUrl) ? fromUrl : 'month';
  });
  const [currentDate, setCurrentDate] = useState(() => {
    const fromUrl = initialParams.get('date');
    const parsed = fromUrl ? dayjs(fromUrl) : null;
    return parsed && parsed.isValid() ? parsed : dayjs();
  });

  /** `/calendar?view=month&date=2026-03-01` - where the user actually is. */
  const calendarUrl = useMemo(
    () => `/calendar?view=${calendarViewMode}&date=${currentDate.format('YYYY-MM-DD')}`,
    [calendarViewMode, currentDate]
  );

  useEffect(() => {
    const target = calendarUrl.slice('/calendar'.length);
    if (location.pathname === '/calendar' && location.search !== target) {
      history.replace(calendarUrl);
    }
  }, [calendarUrl, history, location.pathname, location.search]);
  const [showFeedUrl, setShowFeedUrl] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'calendar',
    steps: calendarTourSteps,
    readyToShow: !loading,
    autoStartDelay: 600,
  });

  /* ─── Callbacks ─── */

  const handleRangeChange = useCallback(
    (start: Date, end: Date) => {
      dispatch(fetchCalendarEvents(start, end, filters));
    },
    [dispatch, filters]
  );

  const navigateToSource = useCallback(
    (event: CalendarEvent) => {
      dispatch(hideModal());
      const base =
        event.source_type === 'task' ? '/tasks'
          : event.source_type === 'matter' ? '/matters'
            : event.source_type === 'project' ? '/projects'
              : null;
      if (!base || !event.source_uuid) return;
      // Carry the origin so the destination can offer a way back to the
      // calendar - on the month and view that were being read (cal-4).
      history.push(`${base}/${event.source_uuid}`, {
        from: calendarUrl,
        fromLabel: t('Calendar'),
      });
    },
    [dispatch, history, calendarUrl, t]
  );

  const handleSelectEvent = useCallback(
    (event: CalendarEvent) => {
      dispatch(
        showModal({
          showModal: true,
          type: 'custom',
          message: event.title || t('Event details'),
          width: '50%',
          content: (
            <EventDetailContent
              event={event}
              onNavigate={navigateToSource}
              onClose={() => dispatch(hideModal())}
            />
          ),
        } as any)
      );
    },
    [dispatch, navigateToSource]
  );

  const handleEventDrop = useCallback(
    (eventId: string, newDeadline: Date) => {
      const event = events.find(
        (e: CalendarEvent) => String(e.id) === eventId || e.uuid === eventId
      );
      if (event) {
        dispatch(updateCalendarEvent(event.uuid, { start_time: newDeadline.toISOString() }));
      }
    },
    [dispatch, events]
  );

  const getDateRange = useCallback(() => {
    if (calendarViewMode === 'month') {
      return {
        start: currentDate.startOf('month').startOf('week').toDate(),
        end: currentDate.endOf('month').endOf('week').toDate(),
      };
    } else if (calendarViewMode === 'week') {
      return {
        start: currentDate.startOf('week').toDate(),
        end: currentDate.endOf('week').toDate(),
      };
    } else if (calendarViewMode === 'day') {
      return {
        start: currentDate.startOf('day').toDate(),
        end: currentDate.endOf('day').toDate(),
      };
    }
    return {
      start: currentDate.startOf('year').toDate(),
      end: currentDate.endOf('year').toDate(),
    };
  }, [calendarViewMode, currentDate]);

  /* ─── Toolbar handlers ─── */

  const handleExport = async () => {
    const range = getDateRange();
    const blob = await exportCalendarICS(range.start, range.end);
    if (blob) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pons-calendar-${dayjs().format('YYYY-MM-DD')}.ics`;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      dispatch(importCalendarICS(file));
      setTimeout(() => {
        const range = getDateRange();
        dispatch(fetchCalendarEvents(range.start, range.end, filters));
      }, 2000);
    }
  };

  const handleSync = () => {
    dispatch(syncCalendarEvents());
    setTimeout(() => {
      const range = getDateRange();
      dispatch(fetchCalendarEvents(range.start, range.end, filters));
    }, 3000);
  };

  const handleCreateFeedToken = () => {
    dispatch(createFeedToken('My Calendar Feed'));
    setShowFeedUrl(true);
  };

  const handleFilterChange = (key: string, value: string | undefined) => {
    dispatch(setCalendarFilters({ [key]: value || undefined }));
  };

  /* ─── Create a manual event (SM-1) ─── */
  const openCreateEvent = useCallback(() => {
    dispatch(
      showModal({
        showModal: true,
        type: 'custom',
        message: t('New event'),
        width: '36rem',
        content: (
          <CreateEventContent
            initialDate={currentDate}
            onClose={() => dispatch(hideModal())}
            onCreate={async (payload) => {
              await dispatch(createCalendarEvent(payload));
              const range = getDateRange();
              dispatch(fetchCalendarEvents(range.start, range.end, filters));
            }}
          />
        ),
      } as any)
    );
  }, [dispatch, t, currentDate, getDateRange, filters]);

  // The sidebar quick-"+" lands here as `?add=true`; open the create modal
  // every time it appears (B3) - the hook strips the param so a refresh or
  // Back does not reopen it.
  useAddSignal(openCreateEvent);

  /* ─── The occasional tools, folded into the toolbar's overflow menu ─── */

  const overflowActions: Action[] = useMemo(
    () => [
      {
        label: t('Sync'),
        onClick: handleSync,
        icon: (
          <PiArrowsClockwiseLight
            className={`h-4 w-4 ${syncStatus === 'syncing' ? 'animate-spin' : ''}`}
          />
        ),
        variant: 'outline' as const,
        disabled: syncStatus === 'syncing',
      },
      {
        label: t('Export'),
        onClick: handleExport,
        icon: <PiDownloadSimpleLight className="h-4 w-4" />,
        variant: 'outline' as const,
      },
      {
        label: t('Import'),
        onClick: () => fileInputRef.current?.click(),
        icon: <PiUploadSimpleLight className="h-4 w-4" />,
        variant: 'outline' as const,
      },
      {
        label: t('Subscribe'),
        onClick: handleCreateFeedToken,
        icon: <PiLinkLight className="h-4 w-4" />,
        variant: 'outline' as const,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, syncStatus, handleExport, handleSync, handleCreateFeedToken]
  );

  /* ─── The event-type key, now a popover in the toolbar rather than a strip ─── */

  const legend = useMemo(
    () => (
      <div data-tour-id="calendar-legend" className="flex flex-col gap-1">
        {LEGEND_TYPES.map((type) => {
          const colors = EVENT_TYPE_COLORS[type];
          const label = EVENT_TYPE_LABELS[type] ?? type;
          return (
            <span key={type} className="inline-flex items-center gap-2 text-xs text-fg-body">
              <span className={`inline-block h-1.5 w-1.5 rounded-full ${colors.dot}`} />
              {t(label)}
            </span>
          );
        })}
      </div>
    ),
    [t]
  );

  const activeFilterCount = useMemo(
    () => ['event_type', 'source_type', 'status'].filter((key) => filters?.[key]).length,
    [filters]
  );

  /* ─── Below the toolbar: the filter panel and the subscribe URL ─── */

  const filterBar = useMemo(() => {
    const typeOptions = [
      { value: '', label: t('All types') },
      ...FILTER_EVENT_TYPES.map((type) => ({
        value: type,
        label: EVENT_TYPE_LABELS[type] ?? type,
      })),
    ];
    const sourceOptions = [
      { value: '', label: t('All sources') },
      ...SOURCE_TYPES.map((source) => ({
        value: source,
        label: source.charAt(0).toUpperCase() + source.slice(1),
      })),
    ];
    const statusOptions = [
      { value: '', label: t('Any status') },
      { value: 'active', label: t('Active') },
      { value: 'tentative', label: t('Tentative') },
      { value: 'completed', label: t('Completed') },
      { value: 'cancelled', label: t('Cancelled') },
    ];

    return (
      <>
        {/* Filters - the same shared controls every other list page filters with. */}
        {showFilters && (
          <div className="glass ms-auto me-4 mt-2 flex w-fit max-w-[calc(100%-2rem)] flex-wrap items-end gap-3 rounded-lg px-4 py-3 shadow-lg">
            <Dropdown
              label={t('Type')}
              options={typeOptions}
              value={filters?.event_type || ''}
              onChange={(v) => handleFilterChange('event_type', v as string)}
              width="w-48"
              thinner
            />
            <Dropdown
              label={t('Source')}
              options={sourceOptions}
              value={filters?.source_type || ''}
              onChange={(v) => handleFilterChange('source_type', v as string)}
              width="w-48"
              thinner
            />
            <Dropdown
              label={t('Status')}
              options={statusOptions}
              value={filters?.status || ''}
              onChange={(v) => handleFilterChange('status', v as string)}
              width="w-48"
              thinner
            />
            <Button
              variant="neutral"
              size="small"
              onClick={() => {
                handleFilterChange('event_type', undefined);
                handleFilterChange('source_type', undefined);
                handleFilterChange('status', undefined);
              }}
            >
              {t('Clear')}
            </Button>
          </div>
        )}

        {/* Subscribe URL */}
        {showFeedUrl && feedToken && (
          <div className="flex items-center gap-2 border-b border-hairline bg-success-50/60 dark:bg-success-900/15 px-4 py-2">
            <PiInfoLight className="h-4 w-4 flex-shrink-0 text-success-600 dark:text-success-300" />
            <span className="text-xs text-success-700 dark:text-success-300">
              {t('Subscribe URL (add to Outlook / Google Calendar):')}
            </span>
            <code className="flex-1 truncate rounded border border-hairline bg-surface-sunken/70 px-2 py-1 font-mono text-xs text-fg-body">
              {feedToken}
            </code>
            <Button
              variant="outline"
              size="small"
              onClick={() => navigator.clipboard.writeText(feedToken)}
              icon={<PiCopyLight className="h-4 w-4" />}
            >
              {t('Copy')}
            </Button>
            <button
              onClick={() => setShowFeedUrl(false)}
              className="rounded p-1 text-fg-muted transition-colors hover:bg-surface-sunken/70 hover:text-fg-strong"
              aria-label={t('Close')}
            >
              <PiXLight className="h-4 w-4" />
            </button>
          </div>
        )}
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedToken, showFeedUrl, showFilters, filters, t]);

  /* ─── Render ─── */

  return (
    <div className="flex flex-col h-full">
      <CalendarView
        tasks={events ?? []}
        currentDate={currentDate}
        onRangeChange={handleRangeChange}
        onSelectEvent={handleSelectEvent}
        onEventDrop={handleEventDrop}
        onCalendarViewModeChange={setCalendarViewMode}
        calendarViewMode={calendarViewMode}
        onDateChange={setCurrentDate}
        onAddTask={openCreateEvent}
        isDayLoading={loading}
        overflowActions={overflowActions}
        legend={legend}
        filterControl={{
          open: showFilters,
          activeCount: activeFilterCount,
          onToggle: () => setShowFilters((v) => !v),
        }}
        filterBar={filterBar}
      />

      {/* Hidden file input for .ics import */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".ics"
        onChange={handleImport}
        className="hidden"
      />

      <FeatureTour
        steps={calendarTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </div>
  );
}

export default CalendarViewPage;